import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehiclePeripheralMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

@Injectable({
    providedIn: 'root'
})
export class FoundationVehiclesService {

    private activeVehiclesCacheName: string = "activeVehiclesCache";
    private allVehiclesCacheName: string = "allVehiclesCache";

    constructor(
        private readonly http: HttpClient,
        private readonly dataCacheService: DataCacheService
    ) { }

    getVehicles(activeOnly: boolean = false, invalidateCache = false): Observable<IVehicle[]> {
        const cacheToRetrieve = activeOnly ? this.activeVehiclesCacheName : this.allVehiclesCacheName;
        return this.dataCacheService.getOrRequest(cacheToRetrieve, () => this.getVehiclesFromDb(activeOnly), { invalidateCache });
    }

    getVehicle(vehicleId: number): Observable<IVehicle> {
        return this.http.get<IVehicle>(`/api/foundation/vehicles/${vehicleId}`);
    }

    getVehiclesExtended(): Observable<IVehicle[]> {
        return this.http.get<IVehicle[]>(`/api/foundation/vehicles/extendedinfo/`);
    }

    getPeripheralMapping(serial: string): Observable<IVehiclePeripheralMapping> {
        return this.http.get<IVehiclePeripheralMapping>(`/api/foundation/vehicles/peripheral/${serial}`);
    }

    getVehicleUnitLocation(vehicleId: number): Observable<string> {
        return this.http.get(`/api/foundation/vehicles/${vehicleId}/unitlocation`, {responseType: 'text'});
    }

    createUpdateVehicle(vehicle: IVehicle): Observable<IVehicle> {
        return this.http.post<IVehicle>(`/api/foundation/vehicles/`, vehicle);
    }

    updateVehicleData(vehicle: IVehicle): Observable<IVehicle> {
        return this.http.post<IVehicle>(`/api/foundation/vehicles/updatevehicledata`, vehicle);
    }

    getVehiclesForAccount(accountId: number): Observable<IVehicle[]> {
        return this.getVehicles().pipe(map(vehicles => {
            return vehicles.filter(vehicle => Number(vehicle.AccountId) === Number(accountId));
        }))
    }

    createVehicleMap(vehicles: IVehicle[]): Map<number, IVehicle> {
        return new Map(vehicles.map(a => [a.Id, a]));
    }

    doesVehicleExist(accountId: number, vehicleReg: string): Observable<boolean> {
        return this.http.get<boolean>(`/api/foundation/vehicles/vehicleexists/${accountId}/${vehicleReg}`);        
    }

    private getVehiclesFromDb(activeOnly: boolean = false): Observable<IVehicle[]> {
        return this.http.get<IVehicle[]>(`/api/foundation/vehicles/`, { params: { activeVehiclesOnly: activeOnly } });
    }
}
